import React, { useState, useMemo } from "react";
import { useHR } from "../HRContext";
import {
  HREmployee,
  EmployeeFilters,
  HRDepartment,
  HRPosition,
} from "../../types/hr";
import EmployeeForm from "./EmployeeForm";
import EmployeeDetails from "./EmployeeDetails";
import EmployeeAvatar from "./EmployeeAvatar";
import { EmployeePayrollItems } from "../payroll-items";
import {
  Layout,
  Card,
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Avatar,
  Row,
  Col,
  Typography,
  Empty,
  Spin,
  Modal,
  message,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ClearOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const EmployeeList: React.FC = () => {
  const { employees, departments, positions, loading, deleteEmployee } =
    useHR();

  const [filters, setFilters] = useState<EmployeeFilters>({});
  const [selectedEmployee, setSelectedEmployee] = useState<HREmployee | null>(
    null
  );
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<HREmployee | null>(
    null
  );
  const [showDetails, setShowDetails] = useState(false);
  const [showPayrollItems, setShowPayrollItems] = useState(false);
  const [payrollEmployee, setPayrollEmployee] = useState<HREmployee | null>(
    null
  );

  // Filtrage des employés
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee: HREmployee) => {
      // Filtre de recherche
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          employee.first_name?.toLowerCase().includes(searchLower) ||
          employee.last_name?.toLowerCase().includes(searchLower) ||
          employee.employee_number?.toLowerCase().includes(searchLower) ||
          employee.email?.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      // Filtre par département
      if (
        filters.department_id &&
        employee.department_id !== filters.department_id
      ) {
        return false;
      }

      // Filtre par poste
      if (filters.position_id && employee.position_id !== filters.position_id) {
        return false;
      }

      // Filtre par statut
      if (filters.status && employee.status !== filters.status) {
        return false;
      }

      // Filtre par date d'embauche
      if (filters.hire_date_from) {
        const hireDate = new Date(employee.hire_date);
        const fromDate = new Date(filters.hire_date_from);
        if (hireDate < fromDate) return false;
      }

      if (filters.hire_date_to) {
        const hireDate = new Date(employee.hire_date);
        const toDate = new Date(filters.hire_date_to);
        if (hireDate > toDate) return false;
      }

      return true;
    });
  }, [employees, filters]);

  const handleDelete = async (employee: HREmployee) => {
    Modal.confirm({
      title: "Confirmation de suppression",
      content: `Êtes-vous sûr de vouloir supprimer l'employé ${employee.first_name} ${employee.last_name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await deleteEmployee(employee.id);
          message.success("Employé supprimé avec succès");
        } catch (error) {
          message.error("Erreur lors de la suppression de l'employé");
        }
      },
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-BF", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: "green", text: "Actif" },
      inactive: { color: "orange", text: "Inactif" },
      terminated: { color: "red", text: "Terminé" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "default",
      text: status,
    };

    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = Object.values(filters).some((val) => val);

  const columns: ColumnsType<HREmployee> = [
    {
      title: "N° Employé",
      dataIndex: "employee_number",
      key: "employee_number",
      width: 120,
      sorter: (a, b) =>
        (a.employee_number || "").localeCompare(b.employee_number || ""),
    },
    {
      title: "Employé",
      key: "employee",
      width: 250,
      render: (_, employee) => (
        <div className="flex items-center space-x-3">
          <EmployeeAvatar employee={employee} size={40} />
          <div>
            <div className="font-medium">
              {employee.first_name} {employee.last_name}
            </div>
            <Text type="secondary" className="text-sm">
              {employee.email}
            </Text>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
    },
    {
      title: "Département",
      dataIndex: "department_name",
      key: "department_name",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Poste",
      dataIndex: "position_title",
      key: "position_title",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Date d'embauche",
      dataIndex: "hire_date",
      key: "hire_date",
      width: 130,
      render: (date) => formatDate(date),
      sorter: (a, b) =>
        new Date(a.hire_date).getTime() - new Date(b.hire_date).getTime(),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => getStatusTag(status),
      filters: [
        { text: "Actif", value: "active" },
        { text: "Inactif", value: "inactive" },
        { text: "Suspendu", value: "suspended" },
        { text: "Terminé", value: "terminated" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      fixed: "right",
      render: (_, employee) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedEmployee(employee);
              setShowDetails(true);
            }}
            title="Voir les détails"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingEmployee(employee);
              setShowForm(true);
            }}
            title="Modifier"
          />
          <Button
            type="text"
            icon={<WalletOutlined />}
            onClick={() => {
              setPayrollEmployee(employee);
              setShowPayrollItems(true);
            }}
            title="Éléments de paie"
            style={{ color: "#1890ff" }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(employee)}
            title="Supprimer"
          />
        </Space>
      ),
    },
  ];

  if (loading.employees) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: "24px" }}>
        {/* Header */}
        <Card className="mb-6" bordered={false}>
          <div className="flex justify-between items-center">
            <div>
              <Title level={2} className="!mb-2">
                Gestion des Employés
              </Title>
              <Text type="secondary">
                {filteredEmployees.length} employé(s) trouvé(s)
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingEmployee(null);
                setShowForm(true);
              }}
            >
              Nouvel Employé
            </Button>
          </div>
        </Card>

        {/* Filtres */}
        <Card title="Filtres" className="mb-6">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <Input
                placeholder="Rechercher par nom, prénom, email, numéro..."
                prefix={<SearchOutlined />}
                value={filters.search || ""}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <Select
                placeholder="Département"
                value={filters.department_id || undefined}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, department_id: value }))
                }
                allowClear
                style={{ width: "100%" }}
              >
                {departments.map((dept: HRDepartment) => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <Select
                placeholder="Poste"
                value={filters.position_id || undefined}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, position_id: value }))
                }
                allowClear
                style={{ width: "100%" }}
              >
                {positions.map((pos: HRPosition) => (
                  <Option key={pos.id} value={pos.id}>
                    {pos.title}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <Select
                placeholder="Statut"
                value={filters.status || undefined}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, status: value }))
                }
                allowClear
                style={{ width: "100%" }}
              >
                <Option value="active">Actif</Option>
                <Option value="inactive">Inactif</Option>
                <Option value="suspended">Suspendu</Option>
                <Option value="terminated">Terminé</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} lg={4}>
              <RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder={["Date début", "Date fin"]}
                onChange={(dates, dateStrings) => {
                  setFilters((prev) => ({
                    ...prev,
                    hire_date_from: dateStrings[0] || undefined,
                    hire_date_to: dateStrings[1] || undefined,
                  }));
                }}
              />
            </Col>
          </Row>

          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Button
                type="text"
                icon={<ClearOutlined />}
                onClick={clearFilters}
              >
                Effacer tous les filtres
              </Button>
            </div>
          )}
        </Card>

        {/* Tableau */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredEmployees}
            rowKey="id"
            pagination={{
              total: filteredEmployees.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} employés`,
            }}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Aucun employé trouvé"
                />
              ),
            }}
          />
        </Card>

        {/* Modales */}
        {showForm && (
          <EmployeeForm
            employee={editingEmployee}
            onClose={() => {
              setShowForm(false);
              setEditingEmployee(null);
            }}
          />
        )}

        {showDetails && selectedEmployee && (
          <EmployeeDetails
            employee={selectedEmployee}
            onClose={() => {
              setShowDetails(false);
              setSelectedEmployee(null);
            }}
            onEdit={() => {
              setEditingEmployee(selectedEmployee);
              setShowDetails(false);
              setShowForm(true);
            }}
          />
        )}

        {/* Modal Éléments de paie */}
        {payrollEmployee && (
          <EmployeePayrollItems
            employee={payrollEmployee}
            visible={showPayrollItems}
            onClose={() => {
              setShowPayrollItems(false);
              setPayrollEmployee(null);
            }}
          />
        )}
      </Content>
    </Layout>
  );
};

export default EmployeeList;
